/**
 * RuleSetViewerPage — Read-only legislation reference for all roles.
 *
 * DRR-055: Dumb Frontend — renders backend data, zero domain logic.
 * DRR-096: Temporally-valid legislation packages with version chain.
 * Layout: MasterDetailLayout — tabulator table (left) + detail panel (right).
 */

import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  Title,
  Text,
  Group,
  Stack,
  Paper,
  SimpleGrid,
  Card,
  Badge,
  ThemeIcon,
  Loader,
  Alert,
  Anchor,
  TextInput,
  Divider,
  ScrollArea,
  ActionIcon,
  Tooltip,
  MultiSelect,
  Select,
  Popover,
  UnstyledButton,
  Menu,
  Table,
  Accordion,
  Code,
} from '@mantine/core';
import {
  IconScale,
  IconAlertTriangle,
  IconShieldCheck,
  IconClock,
  IconArrowRight,
  IconCircleCheck,
  IconCircleX,
  IconSearch,
  IconX,
  IconLink,
  IconColumns,
  IconDownload,
  IconCheck,
  IconFilter,
  IconFileText,
  IconSignature,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useTenantId } from '@/auth';
import {
  useListRuleSetsQuery,
  useGenerateRulesetComplianceReportMutation,
  useGenerateRulesetAcknowledgmentMutation,
  type ListRuleSetsQuery,
} from '@/generated/graphql';

import { useComplianceFilterDimensions } from './hooks/useComplianceFilterDimensions';
import { Tabulator } from '@/types/tabulator';
import 'tabulator-tables/dist/css/tabulator.min.css';
import '@/styles/tabulator-mantine.css';
import { TABULATOR_DEFAULTS } from '@/utils/tabulatorConfig';
import { MasterDetailLayout } from '@/components/shared/MasterDetailLayout';
import { EntityMediaHeader } from '@/components/shared/EntityMediaHeader';
import { formatFieldName, FieldValue } from '@/components/RecordDetailDrawer';

// SUBGRAPH_TO_INDUSTRIES removed — was a hardcoded frontend industry map.
// Filter dimensions now come from complianceFilterDimensions backend query.
// DRR-055/074: frontend never defines filter values.

// ============================================================================
// TYPES
// ============================================================================

type RuleSetItem = NonNullable<ListRuleSetsQuery['ruleSets']>[number];

/** Flattened row fed to Tabulator — avoids duplicate field names and frozen objects */
interface RuleSetRow {
  rulesetId: string;
  displayName: string;
  jurisdiction: string;
  safetyLevel: string;
  isLifeCritical: boolean;
  ruleCount: number;
  isCurrentlyEffective: boolean;
  effectiveDate: string | null | undefined;
  sunsetDate: string | null | undefined;
  supersedes: string | null | undefined;
  supersededBy: string | null | undefined;
  category: string;
  setting: string;
  // Flattened from sourceLegislation (avoids duplicate `field` in Tabulator)
  statuteShortCite: string;
  legislationAuthority: string;
  legislationUrl: string;
  // Facility type / industry tags for filter matching
  appliesToIndustries: string[];
  // Applies-when flag conditions for filter matching
  appliesWhenFlags: string[];
  // Keep original for detail panel access
  _original: RuleSetItem;
}

function toRow(rs: RuleSetItem): RuleSetRow {
  const sl = rs.sourceLegislation;
  return {
    rulesetId: rs.rulesetId,
    displayName: rs.displayName,
    jurisdiction: rs.jurisdiction,
    safetyLevel: rs.safetyLevel,
    isLifeCritical: rs.isLifeCritical,
    ruleCount: rs.ruleCount,
    isCurrentlyEffective: rs.isCurrentlyEffective,
    effectiveDate: rs.effectiveDate,
    sunsetDate: rs.sunsetDate,
    supersedes: rs.supersedes,
    supersededBy: rs.supersededBy,
    category: rs.category,
    setting: rs.setting,
    statuteShortCite: sl?.shortCite ?? '',
    legislationAuthority: sl?.authority ?? '',
    legislationUrl: sl?.url ?? '',
    appliesToIndustries: rs.appliesToIndustries ?? [],
    appliesWhenFlags: (rs as Record<string, unknown>).appliesWhenFlags as string[] ?? [],
    _original: rs,
  };
}

// ============================================================================
// HELPERS
// ============================================================================

function safetyColor(level: string): string {
  switch (level.toLowerCase()) {
    case 'critical': return 'red';
    case 'high': return 'orange';
    case 'elevated': return 'yellow';
    default: return 'gray';
  }
}

function effectivenessLabel(rs: RuleSetItem | RuleSetRow): { label: string; color: string } {
  if (rs.supersededBy) return { label: 'Superseded', color: '#868e96' };
  if (rs.isCurrentlyEffective === false) return { label: 'Pending', color: '#f59f00' };
  if (rs.isCurrentlyEffective) return { label: 'Active', color: '#37b24d' };
  return { label: 'Unknown', color: '#868e96' };
}

function EffectivenessBadge({ rs }: { readonly rs: RuleSetItem }) {
  if (rs.supersededBy)
    return <Badge color="gray" size="xs" variant="light" leftSection={<IconCircleX size={10} />}>Superseded</Badge>;
  if (rs.isCurrentlyEffective === false)
    return <Badge color="yellow" size="xs" variant="light" leftSection={<IconClock size={10} />}>Not Yet Effective</Badge>;
  if (rs.isCurrentlyEffective)
    return <Badge color="green" size="xs" variant="light" leftSection={<IconCircleCheck size={10} />}>Active</Badge>;
  return null;
}

// ============================================================================
// COLUMN DEFINITIONS (unique field names — no duplicates)
// ============================================================================

type ColDef = {
  title: string;
  field: string;
  defaultVisible: boolean;
  essential?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

const ALL_COLUMNS: ColDef[] = [
  {
    title: '',
    field: '__sel',
    formatter: 'rowSelection',
    titleFormatter: 'rowSelection',
    hozAlign: 'center',
    headerHozAlign: 'center',
    width: 30,
    headerSort: false,
    frozen: true,
    defaultVisible: true,
    essential: true,
  },
  {
    title: 'Name',
    field: 'displayName',
    minWidth: 160,
    defaultVisible: true,
    essential: true,
    formatter: (cell: { getData: () => RuleSetRow; getValue: () => string }) => {
      const row = cell.getData();
      const name = cell.getValue() ?? '';
      const lc = row.isLifeCritical
        ? `<span style="display:inline-block;background:#ffe3e3;color:#c92a2a;font-size:10px;padding:0 4px;border-radius:3px;margin-left:4px;font-weight:600">LIFE</span>`
        : '';
      return `<div style="font-weight:500;font-size:13px">${name}${lc}</div>`;
    },
    sorter: 'string',
  },
  {
    title: 'Jurisdiction',
    field: 'jurisdiction',
    width: 90,
    defaultVisible: true,
    formatter: (cell: { getValue: () => string }) =>
      `<span style="border:1px solid #dee2e6;border-radius:4px;padding:1px 6px;font-size:11px">${cell.getValue() ?? '—'}</span>`,
  },
  {
    title: 'Statute',
    field: 'statuteShortCite',
    minWidth: 90,
    defaultVisible: true,
    sorter: 'string',
    formatter: (cell: { getValue: () => string }) => {
      const v = cell.getValue();
      return v || '<span style="color:#868e96">—</span>';
    },
  },
  {
    title: 'Authority',
    field: 'legislationAuthority',
    minWidth: 120,
    defaultVisible: false,
    sorter: 'string',
    formatter: (cell: { getValue: () => string }) => {
      const v = cell.getValue();
      return v ? `<span style="font-size:12px">${v}</span>` : '<span style="color:#868e96">—</span>';
    },
  },
  {
    title: 'Law',
    field: 'legislationUrl',
    width: 40,
    hozAlign: 'center',
    headerSort: false,
    defaultVisible: true,
    formatter: (cell: { getValue: () => string }) => {
      const url = cell.getValue();
      if (!url) return '';
      return `<a href="${url}" target="_blank" rel="noreferrer" title="${url}" onclick="event.stopPropagation()" style="color:var(--mantine-color-blue-6);line-height:1">
        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
      </a>`;
    },
  },
  {
    title: 'Safety',
    field: 'safetyLevel',
    width: 80,
    defaultVisible: true,
    formatter: (cell: { getValue: () => string }) => {
      const level = (cell.getValue() ?? '').toLowerCase();
      const colors: Record<string, string> = { critical: '#c92a2a', high: '#e8590c', elevated: '#f59f00' };
      const bg: Record<string, string> = { critical: '#ffe3e3', high: '#ffe8cc', elevated: '#fff9db' };
      const c = colors[level] ?? '#868e96';
      const b = bg[level] ?? '#f1f3f5';
      return `<span style="background:${b};color:${c};font-size:11px;padding:1px 6px;border-radius:4px;font-weight:600">${level}</span>`;
    },
  },
  {
    title: 'Rules',
    field: 'ruleCount',
    width: 60,
    hozAlign: 'center',
    defaultVisible: true,
    formatter: (cell: { getValue: () => number }) =>
      `<span style="background:#e7f5ff;color:#1971c2;font-size:11px;padding:1px 6px;border-radius:4px">${cell.getValue()}</span>`,
  },
  {
    title: 'Status',
    field: 'isCurrentlyEffective',
    width: 90,
    defaultVisible: true,
    formatter: (cell: { getData: () => RuleSetRow }) => {
      const { label, color } = effectivenessLabel(cell.getData());
      return `<span style="color:${color};font-size:11px;font-weight:600">${label}</span>`;
    },
  },
  {
    title: 'Effective',
    field: 'effectiveDate',
    width: 100,
    defaultVisible: true,
    formatter: (cell: { getValue: () => string | null }) => {
      const v = cell.getValue();
      return v ? `<span style="font-size:12px">${v}</span>` : '<span style="color:#868e96">—</span>';
    },
  },
  {
    title: 'Sunset',
    field: 'sunsetDate',
    width: 95,
    defaultVisible: false,
    formatter: (cell: { getValue: () => string | null }) => {
      const v = cell.getValue();
      return v ? `<span style="font-size:12px;color:#e8590c">${v}</span>` : '<span style="color:#868e96">—</span>';
    },
  },
  {
    title: 'Category',
    field: 'category',
    width: 90,
    defaultVisible: false,
    formatter: (cell: { getValue: () => string }) =>
      `<span style="font-size:12px">${cell.getValue() ?? '—'}</span>`,
  },
  {
    title: 'Chain',
    field: 'supersedes',
    width: 50,
    hozAlign: 'center',
    defaultVisible: true,
    formatter: (cell: { getData: () => RuleSetRow }) => {
      const row = cell.getData();
      if (row.supersedes)
        return `<span title="Supersedes ${row.supersedes}" style="color:#868e96;font-size:13px">↑</span>`;
      if (row.supersededBy)
        return `<span title="Superseded by ${row.supersededBy}" style="color:#1971c2;font-size:13px">↓</span>`;
      return '';
    },
  },
];

/** Strip custom metadata props before passing column defs to Tabulator */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function toTabulatorCols(cols: ColDef[]) {
  return cols.map(({ defaultVisible: _dv, essential: _ess, ...rest }) => rest);
}

// ============================================================================
// RULESET TABLE
// ============================================================================

interface RuleSetTableProps {
  rulesets: RuleSetItem[];
  onSelect: (rs: RuleSetItem | null) => void;
  selected: RuleSetItem | null;
}

function RuleSetTable({ rulesets, onSelect, selected }: RuleSetTableProps) {
  const tableRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tabulatorRef = useRef<any>(null);
  const tableReadyRef = useRef(false);
  const latestRowsRef = useRef<RuleSetRow[]>([]);
  // Use ref for onSelect to avoid stale closures in tabulator event handlers
  const onSelectRef = useRef(onSelect);
  useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);

  // Backend-driven filter dimensions — chips rendered from this, never hardcoded
  const { dimensions: filterDimensions } = useComplianceFilterDimensions();

  const [filters, setFilters] = useState({
    search: '',
    jurisdictions: [] as string[],
    safetyLevels: [] as string[],
    statuses: [] as string[],
    facilityTypes: [] as string[],
    appliesWhen: [] as string[],
    legislation: [] as string[],
  });
  const [groupBy, setGroupBy] = useState('');
  const [columnSearch, setColumnSearch] = useState('');
  const [colPopoverOpen, setColPopoverOpen] = useState(false);
  const [colVisible, setColVisible] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(ALL_COLUMNS.map((c) => [c.title, c.defaultVisible])),
  );

  // Jurisdiction values from backend dimensions (DRR-055: never derive from dataset in frontend)
  const availableJurisdictions = useMemo(
    () =>
      (filterDimensions.find((d) => d.dimension === 'jurisdiction')?.values ?? []).map((v) => ({
        value: v.value,
        label: `${v.label} (${v.count})`,
      })),
    [filterDimensions],
  );

  const toggleCol = useCallback((title: string) => {
    setColVisible((prev) => ({ ...prev, [title]: !prev[title] }));
  }, []);

  const filteredColList = useMemo(() => {
    const q = columnSearch.toLowerCase();
    return ALL_COLUMNS.filter((c) => !c.essential && c.title && c.title.toLowerCase().includes(q));
  }, [columnSearch]);

  const activeColumns = useMemo(
    () => ALL_COLUMNS.filter((c) => colVisible[c.title] !== false),
    [colVisible],
  );

  // Rebuild columns in running tabulator
  useEffect(() => {
    if (!tabulatorRef.current || !tableReadyRef.current) return;
    tabulatorRef.current.setColumns(toTabulatorCols(activeColumns));
    tabulatorRef.current.replaceData(latestRowsRef.current);
  }, [activeColumns]);

  // Create tabulator once on mount
  useEffect(() => {
    if (!tableRef.current) return;
    tableReadyRef.current = false;

    const rows = rulesets.map(toRow);
    latestRowsRef.current = rows;

    const tab = new Tabulator(tableRef.current, {
      ...TABULATOR_DEFAULTS,
      data: rows,
      columns: toTabulatorCols(activeColumns),
      selectable: false, // Use rowClick only — avoids rowSelection conflicts
      height: '100%',
      layout: 'fitColumns',
      placeholder: 'No RuleSets found',
    });

    tab.on('tableBuilt', () => {
      tableReadyRef.current = true;
      // Reload data in case it changed while building
      if (latestRowsRef.current.length !== rows.length || latestRowsRef.current !== rows) {
        tab.replaceData(latestRowsRef.current);
      }
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tab.on('rowClick', (_e: Event, row: any) => {
      const rowData = row.getData() as RuleSetRow;
      onSelectRef.current(rowData._original);

      // Highlight selected row
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      tab.getRows().forEach((r: any) => {
        const el = r.getElement() as HTMLElement;
        el.style.background = r === row ? 'var(--mantine-color-blue-0)' : '';
      });
    });

    tabulatorRef.current = tab;
    return () => {
      tableReadyRef.current = false;
      tab.destroy();
      tabulatorRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync data changes
  useEffect(() => {
    const rows = rulesets.map(toRow);
    latestRowsRef.current = rows;
    if (tabulatorRef.current && tableReadyRef.current) {
      tabulatorRef.current.replaceData(rows);
    }
  }, [rulesets]);

  // Re-highlight when selected changes from outside (e.g. version chain navigation)
  useEffect(() => {
    if (!tabulatorRef.current || !tableReadyRef.current) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tabulatorRef.current.getRows().forEach((row: any) => {
      const data = row.getData() as RuleSetRow;
      const el = row.getElement() as HTMLElement;
      el.style.background =
        selected && data.rulesetId === selected.rulesetId ? 'var(--mantine-color-blue-0)' : '';
    });
  }, [selected]);

  const applyFilters = useCallback((next: typeof filters) => {
    if (!tabulatorRef.current) return;
    const { search, jurisdictions, safetyLevels, statuses, facilityTypes, appliesWhen, legislation } = next;
    const hasAny =
      search.trim() ||
      jurisdictions.length ||
      safetyLevels.length ||
      statuses.length ||
      facilityTypes.length ||
      appliesWhen.length ||
      legislation.length;
    if (!hasAny) {
      tabulatorRef.current.clearFilter();
      return;
    }
    tabulatorRef.current.setFilter((row: RuleSetRow) => {
      if (search.trim()) {
        const q = search.toLowerCase();
        const hit =
          row.displayName.toLowerCase().includes(q) ||
          row.rulesetId.toLowerCase().includes(q) ||
          row.jurisdiction.toLowerCase().includes(q) ||
          row.statuteShortCite.toLowerCase().includes(q) ||
          row.legislationAuthority.toLowerCase().includes(q);
        if (!hit) return false;
      }
      if (jurisdictions.length && !jurisdictions.includes(row.jurisdiction)) return false;
      if (safetyLevels.length && !safetyLevels.includes(row.safetyLevel.toLowerCase())) return false;
      if (statuses.length) {
        const st = row.supersededBy ? 'superseded' : row.isCurrentlyEffective ? 'active' : 'pending';
        if (!statuses.includes(st)) return false;
      }
      // Facility type: match against appliesToIndustries (case-insensitive)
      if (facilityTypes.length) {
        const rowFt = row.appliesToIndustries.map((i) => i.toLowerCase());
        if (!facilityTypes.some((ft) => rowFt.includes(ft.toLowerCase()))) return false;
      }
      // Applies-when: match against appliesWhenFlags
      if (appliesWhen.length) {
        const rowAw = row.appliesWhenFlags.map((f) => f.toLowerCase());
        if (!appliesWhen.some((aw) => rowAw.includes(aw.toLowerCase()))) return false;
      }
      // Legislation: match against statute short cite
      if (legislation.length) {
        if (!legislation.some((l) => row.statuteShortCite.toLowerCase().includes(l.toLowerCase()))) return false;
      }
      return true;
    });
  }, []);

  const updateFilter = useCallback(
    <K extends keyof typeof filters>(key: K, value: (typeof filters)[K]) => {
      setFilters((prev) => {
        const next = { ...prev, [key]: value };
        applyFilters(next);
        return next;
      });
    },
    [applyFilters],
  );

  const toggleChip = useCallback(
    (key: 'safetyLevels' | 'statuses' | 'facilityTypes' | 'appliesWhen' | 'legislation', value: string) => {
      setFilters((prev) => {
        const arr = prev[key];
        const next = { ...prev, [key]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] };
        applyFilters(next);
        return next;
      });
    },
    [applyFilters],
  );

  const handleGroupBy = useCallback((value: string | null) => {
    const field = value ?? '';
    setGroupBy(field);
    if (!tabulatorRef.current) return;
    if (field) {
      tabulatorRef.current.setGroupBy(field);
    } else {
      tabulatorRef.current.setGroupBy('');
    }
  }, []);

  const handleExport = useCallback((fmt: 'csv' | 'json') => {
    const ts = new Date().toISOString().slice(0, 10);
    tabulatorRef.current?.download(fmt, `rulesets_${ts}.${fmt}`);
  }, []);

  // GROUP_BY_OPTIONS: jurisdiction label comes from backend dimensions when available
  const GROUP_BY_OPTIONS = [
    { value: '', label: 'No grouping' },
    { value: 'jurisdiction', label: 'Jurisdiction' },
    { value: 'safetyLevel', label: 'Safety Level' },
    { value: 'category', label: 'Category' },
    { value: 'legislationAuthority', label: 'Authority' },
    { value: 'statuteShortCite', label: 'Legislation' },
  ];

  // Map each backend dimension to the filter state key it controls
  const DIMENSION_TO_FILTER_KEY: Record<string, 'safetyLevels' | 'statuses' | 'facilityTypes' | 'appliesWhen' | 'legislation'> = {
    safety_level: 'safetyLevels',
    status: 'statuses',
    facility_type: 'facilityTypes',
    applies_when: 'appliesWhen',
    legislation: 'legislation',
  };

  // Chip color hints per dimension value (backend authoritative labels; colors are presentation only)
  const chipColor = (dimension: string, value: string): string => {
    if (dimension === 'safety_level') {
      return { critical: 'red', high: 'orange', elevated: 'yellow', standard: 'gray', low: 'gray' }[value] ?? 'gray';
    }
    if (dimension === 'status') {
      return { active: 'green', pending: 'yellow', superseded: 'gray' }[value] ?? 'gray';
    }
    return 'blue';
  };

  const activeFilterCount =
    filters.jurisdictions.length +
    filters.safetyLevels.length +
    filters.statuses.length +
    filters.facilityTypes.length +
    filters.appliesWhen.length +
    filters.legislation.length;

  return (
    <Stack gap={0} h="100%">
      {/* Toolbar row 1: search + utility buttons */}
      <Group
        gap="xs"
        px="sm"
        pt="xs"
        pb={6}
        style={{ borderBottom: '1px solid var(--mantine-color-default-border)', flexShrink: 0, flexWrap: 'wrap' }}
      >
        <TextInput
          size="xs"
          placeholder="Search name, statute, authority..."
          leftSection={<IconSearch size={13} />}
          rightSection={
            filters.search ? (
              <ActionIcon size="xs" variant="subtle" onClick={() => updateFilter('search', '')}>
                <IconX size={12} />
              </ActionIcon>
            ) : null
          }
          value={filters.search}
          onChange={(e) => updateFilter('search', e.target.value)}
          style={{ flex: 1, minWidth: 160 }}
        />

        {/* Jurisdiction multi-select */}
        <MultiSelect
          size="xs"
          placeholder="Jurisdiction"
          data={availableJurisdictions}
          value={filters.jurisdictions}
          onChange={(v) => updateFilter('jurisdictions', v)}
          clearable
          searchable
          maxDropdownHeight={200}
          style={{ width: 160 }}
          comboboxProps={{ withinPortal: true }}
        />

        {/* Group by */}
        <Select
          size="xs"
          placeholder="Group by..."
          data={GROUP_BY_OPTIONS}
          value={groupBy || ''}
          onChange={handleGroupBy}
          clearable
          style={{ width: 140 }}
          comboboxProps={{ withinPortal: true }}
          leftSection={<IconFilter size={12} />}
        />

        <Divider orientation="vertical" />

        {/* Column visibility */}
        <Popover
          opened={colPopoverOpen}
          onChange={setColPopoverOpen}
          shadow="md"
          withinPortal
          position="bottom-end"
          onClose={() => setColumnSearch('')}
        >
          <Popover.Target>
            <Tooltip label="Show/hide columns">
              <ActionIcon variant="light" size="md" onClick={() => setColPopoverOpen((o) => !o)}>
                <IconColumns size={15} />
              </ActionIcon>
            </Tooltip>
          </Popover.Target>
          <Popover.Dropdown p={0} style={{ width: 220 }}>
            <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--mantine-color-default-border)' }}>
              <TextInput
                placeholder="Search columns..."
                size="xs"
                leftSection={<IconSearch size={13} />}
                value={columnSearch}
                onChange={(e) => setColumnSearch(e.currentTarget.value)}
              />
            </div>
            <ScrollArea mah={260} offsetScrollbars>
              <Stack gap={0} p="xs">
                {filteredColList.map((col) => (
                  <UnstyledButton
                    key={col.title}
                    px={6}
                    py={4}
                    onClick={() => toggleCol(col.title)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 4 }}
                  >
                    {colVisible[col.title] ? (
                      <IconCheck size={13} color="var(--mantine-color-green-6)" />
                    ) : (
                      <span style={{ width: 13 }} />
                    )}
                    <Text size="sm" c={colVisible[col.title] ? undefined : 'dimmed'}>{col.title}</Text>
                  </UnstyledButton>
                ))}
              </Stack>
            </ScrollArea>
            <Group
              px="sm"
              py={6}
              gap="xs"
              justify="center"
              style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}
            >
              <UnstyledButton px="sm" py={2} onClick={() => setColVisible(Object.fromEntries(ALL_COLUMNS.map((c) => [c.title, true])))}>
                <Text size="xs" c="dimmed">All</Text>
              </UnstyledButton>
              <Divider orientation="vertical" />
              <UnstyledButton px="sm" py={2} onClick={() => setColVisible(Object.fromEntries(ALL_COLUMNS.map((c) => [c.title, c.defaultVisible])))}>
                <Text size="xs" c="dimmed">Reset</Text>
              </UnstyledButton>
            </Group>
          </Popover.Dropdown>
        </Popover>

        {/* Export */}
        <Menu shadow="md" position="bottom-end">
          <Menu.Target>
            <Tooltip label="Export">
              <ActionIcon variant="light" size="md">
                <IconDownload size={15} />
              </ActionIcon>
            </Tooltip>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item onClick={() => handleExport('csv')}>Export CSV</Menu.Item>
            <Menu.Item onClick={() => handleExport('json')}>Export JSON</Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>

      {/* Toolbar row 2: backend-driven filter chips (DRR-055/074 — no hardcoded values) */}
      {filterDimensions
        .filter((dim) => dim.dimension in DIMENSION_TO_FILTER_KEY && dim.values.length > 0)
        .map((dim, dimIdx) => {
          const filterKey = DIMENSION_TO_FILTER_KEY[dim.dimension]!;
          const activeValues = filters[filterKey] ?? [];
          return (
            <Group
              key={dim.dimension}
              gap={6}
              px="sm"
              py={5}
              style={{
                borderBottom: '1px solid var(--mantine-color-default-border)',
                flexShrink: 0,
                flexWrap: 'wrap',
                background: dimIdx % 2 === 0 ? undefined : 'var(--mantine-color-gray-0)',
              }}
            >
              <Text size="xs" c="dimmed" fw={500} w={80} style={{ flexShrink: 0 }}>
                {dim.label}:
              </Text>
              {dim.values.map(({ value, label, count }) => {
                const active = activeValues.includes(value);
                return (
                  <Badge
                    key={value}
                    size="sm"
                    variant={active ? 'filled' : 'outline'}
                    color={active ? chipColor(dim.dimension, value) : 'gray'}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => toggleChip(filterKey, value)}
                  >
                    {label}
                    {count > 0 && (
                      <Text span size="xs" c={active ? 'white' : 'dimmed'} ml={3}>
                        {count}
                      </Text>
                    )}
                  </Badge>
                );
              })}
            </Group>
          );
        })}

      {/* Clear all filters */}
      {activeFilterCount > 0 && (
        <Group
          px="sm"
          py={4}
          style={{ borderBottom: '1px solid var(--mantine-color-default-border)', flexShrink: 0 }}
        >
          <Badge
            size="sm"
            variant="light"
            color="blue"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              const reset = {
                ...filters,
                jurisdictions: [],
                safetyLevels: [],
                statuses: [],
                facilityTypes: [],
                appliesWhen: [],
                legislation: [],
              };
              setFilters(reset);
              applyFilters(reset);
            }}
          >
            Clear {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} ×
          </Badge>
        </Group>
      )}

      <div ref={tableRef} style={{ flex: 1, minHeight: 0 }} />
    </Stack>
  );
}

// ============================================================================
// DETAIL PANEL — matches primitive EntityInlineDetail style
// ============================================================================

/** Fields shown in the "Key Info" section at top of detail panel */
const KEY_FIELDS: Array<{ label: string; field: keyof RuleSetItem }> = [
  { label: 'Jurisdiction', field: 'jurisdiction' },
  { label: 'Category', field: 'category' },
  { label: 'Setting', field: 'setting' },
  { label: 'Safety Level', field: 'safetyLevel' },
  { label: 'Effective Date', field: 'effectiveDate' },
  { label: 'Sunset Date', field: 'sunsetDate' },
  { label: 'Version', field: 'supersedes' }, // placeholder
  { label: 'Supersedes', field: 'supersedes' },
  { label: 'Superseded By', field: 'supersededBy' },
  { label: 'Rule Count', field: 'ruleCount' },
];

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb="xs"
        style={{ letterSpacing: '0.05em', borderBottom: '1px solid var(--mantine-color-default-border)', paddingBottom: 4 }}>
        {title}
      </Text>
      {children}
    </div>
  );
}

function RuleSetDetail({
  ruleset,
  allRulesets,
  onNavigate,
  onClose,
}: {
  readonly ruleset: RuleSetItem;
  readonly allRulesets: RuleSetItem[];
  readonly onNavigate: (rs: RuleSetItem) => void;
  readonly onClose: () => void;
}) {
  const navigate = useNavigate();
  const tenantId = useTenantId() ?? '';
  const [generateReport, { loading: reportLoading }] = useGenerateRulesetComplianceReportMutation();
  const [generateAcknowledgment, { loading: ackLoading }] = useGenerateRulesetAcknowledgmentMutation();

  const handleGenerateReport = async () => {
    try {
      const { data } = await generateReport({
        variables: { input: { tenantId, rulesetId: ruleset.rulesetId } },
      });
      if (data?.generateRulesetComplianceReport.success) {
        notifications.show({
          title: 'Report Generated',
          message: data.generateRulesetComplianceReport.message,
          color: 'green',
        });
        navigate(`/ferp-core/documents/${data.generateRulesetComplianceReport.documentId}`);
      } else {
        notifications.show({
          title: 'Report Failed',
          message: data?.generateRulesetComplianceReport.message ?? 'Unknown error',
          color: 'red',
        });
      }
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: String(err),
        color: 'red',
      });
    }
  };

  const handleGenerateAcknowledgment = async () => {
    try {
      const { data } = await generateAcknowledgment({
        variables: {
          input: {
            tenantId,
            rulesetId: ruleset.rulesetId,
            initiateCeremony: true,
            ceremonyParticipants: [],
          },
        },
      });
      if (data?.generateRulesetAcknowledgment.success) {
        notifications.show({
          title: 'Acknowledgment Generated',
          message: data.generateRulesetAcknowledgment.ceremonyId
            ? `Signing ceremony initiated (${data.generateRulesetAcknowledgment.ceremonyId})`
            : data.generateRulesetAcknowledgment.message,
          color: 'green',
        });
        navigate(`/ferp-core/documents/${data.generateRulesetAcknowledgment.documentId}`);
      } else {
        notifications.show({
          title: 'Acknowledgment Failed',
          message: data?.generateRulesetAcknowledgment.message ?? 'Unknown error',
          color: 'red',
        });
      }
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: String(err),
        color: 'red',
      });
    }
  };

  const sl = ruleset.sourceLegislation;
  const supersedesItem = ruleset.supersedes
    ? allRulesets.find((r) => r.rulesetId === ruleset.supersedes) ?? null
    : null;
  const supersededByItem = ruleset.supersededBy
    ? allRulesets.find((r) => r.rulesetId === ruleset.supersededBy) ?? null
    : null;

  // Fields for the generic key-value table (excluding ones we render specially)
  const skipFields = new Set([
    '__typename', 'rulesetId', 'displayName', 'description', 'isLifeCritical',
    'jurisdiction', 'safetyLevel', 'sourceLegislation', 'memberRules',
    'affectsWorkflows', 'affectsFormSchemas', 'complianceFrameworks',
    'appliesToIndustries', 'overridePolicy', 'dependsOn', 'triggerWorkflows', 'ruleIds',
  ]);

  const genericFields = Object.entries(ruleset).filter(
    ([key]) => !skipFields.has(key) && !key.startsWith('_'),
  );

  return (
    <Stack gap={0} h="100%">
      {/* Header — matches EntityInlineDetail pattern */}
      <Group
        justify="space-between"
        p="sm"
        style={{ borderBottom: '1px solid var(--mantine-color-default-border)', flexShrink: 0 }}
      >
        <EntityMediaHeader
          name={ruleset.displayName}
          subtitle={ruleset.description}
          status={ruleset.supersededBy ? 'SUPERSEDED' : ruleset.isCurrentlyEffective ? 'ACTIVE' : 'PENDING'}
          statusColorMap={{ ACTIVE: 'green', PENDING: 'yellow', SUPERSEDED: 'gray' }}
          safetyLevel={ruleset.safetyLevel.toUpperCase()}
          accentColor="blue"
          Icon={IconScale as React.ComponentType<{ size: number }>}
          compact
          subtitleLines={0}
        />
        <Group gap={4}>
          <Tooltip label="Generate compliance report PDF">
            <ActionIcon
              variant="light"
              size="sm"
              color="blue"
              loading={reportLoading}
              onClick={handleGenerateReport}
            >
              <IconFileText size={14} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Generate acknowledgment for sign-off">
            <ActionIcon
              variant="light"
              size="sm"
              color="violet"
              loading={ackLoading}
              onClick={handleGenerateAcknowledgment}
            >
              <IconSignature size={14} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Close detail">
            <ActionIcon variant="subtle" size="sm" c="gray" onClick={onClose}>
              <IconX size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      <ScrollArea style={{ flex: 1 }} p="sm" offsetScrollbars>
        <Stack gap="lg">

          {/* Life-Critical banner */}
          {ruleset.isLifeCritical && (
            <Paper withBorder p="xs" style={{ background: '#fff5f5', borderColor: '#ffc9c9' }}>
              <Group gap="xs">
                <IconAlertTriangle size={14} color="#c92a2a" />
                <Text size="xs" fw={600} c="red">
                  Life-Critical — Non-compliance may cause patient harm
                </Text>
              </Group>
            </Paper>
          )}

          {/* Source Legislation — prominent */}
          {sl && (
            <DetailSection title="Source Legislation">
              <Stack gap={6}>
                <Group gap="xs" wrap="nowrap">
                  <Text size="sm" fw={700}>{sl.shortCite}</Text>
                  <Text size="xs" c="dimmed" lineClamp={1}>— {sl.statute}</Text>
                </Group>
                <Text size="xs" c="dimmed">Authority: {sl.authority}</Text>
                {sl.lastAmended && (
                  <Text size="xs" c="dimmed">Last amended: {sl.lastAmended}</Text>
                )}
                {sl.sections.length > 0 && (
                  <Group gap={4}>
                    <Text size="xs" c="dimmed">Sections:</Text>
                    {sl.sections.map((s) => (
                      <Badge key={s} variant="outline" size="xs">{s}</Badge>
                    ))}
                  </Group>
                )}
                <Group gap={6} mt={2}>
                  <IconLink size={13} color={sl.url ? 'var(--mantine-color-blue-6)' : 'var(--mantine-color-dimmed)'} />
                  {sl.url ? (
                    <Anchor href={sl.url} target="_blank" size="sm" fw={500}>
                      View Official Statute Text ↗
                    </Anchor>
                  ) : (
                    <Text size="xs" c="dimmed">No URL on file</Text>
                  )}
                </Group>
              </Stack>
            </DetailSection>
          )}

          {/* Key fields — generic table matching RecordDetailDrawer style */}
          <DetailSection title="Details">
            <Table striped highlightOnHover>
              <Table.Tbody>
                {genericFields.map(([key, value]) => (
                  <Table.Tr key={key}>
                    <Table.Td w={150}>
                      <Text size="xs" fw={500} c="dimmed">{formatFieldName(key)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <FieldValue value={value} fieldKey={key} entity={ruleset as unknown as Record<string, unknown>} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </DetailSection>

          {/* Version Chain */}
          {(ruleset.supersedes || ruleset.supersededBy) && (
            <DetailSection title="Version Chain (DRR-096)">
              <Stack gap={6}>
                {ruleset.supersedes && (
                  <Group gap="xs">
                    <IconArrowRight size={12} color="gray" />
                    <Text size="xs" c="dimmed">Supersedes:</Text>
                    {supersedesItem ? (
                      <Anchor size="xs" onClick={() => onNavigate(supersedesItem)} style={{ cursor: 'pointer' }}>
                        {supersedesItem.displayName}
                      </Anchor>
                    ) : (
                      <Badge size="xs" variant="outline">{ruleset.supersedes}</Badge>
                    )}
                  </Group>
                )}
                {ruleset.supersededBy && (
                  <Group gap="xs">
                    <IconArrowRight size={12} color="blue" />
                    <Text size="xs" c="dimmed">Superseded by:</Text>
                    {supersededByItem ? (
                      <Anchor size="xs" onClick={() => onNavigate(supersededByItem)} style={{ cursor: 'pointer' }}>
                        {supersededByItem.displayName}
                      </Anchor>
                    ) : (
                      <Badge size="xs" color="blue" variant="outline">{ruleset.supersededBy}</Badge>
                    )}
                  </Group>
                )}
              </Stack>
            </DetailSection>
          )}

          {/* Member Rules */}
          {ruleset.memberRules.length > 0 && (
            <DetailSection title={`Member Rules (${ruleset.memberRules.length})`}>
              <Accordion variant="separated" chevronPosition="left">
                {ruleset.memberRules.map((rule) => (
                  <Accordion.Item key={rule.ruleId} value={rule.ruleId}>
                    <Accordion.Control>
                      <Group justify="space-between" wrap="nowrap" gap="xs">
                        <Text size="sm" fw={500} style={{ flex: 1 }}>{rule.name}</Text>
                        <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                          {rule.sectionRef && (
                            <Badge variant="outline" size="xs" color="gray">§ {rule.sectionRef}</Badge>
                          )}
                          <Badge
                            color={rule.evidenceStatus === 'complete' ? 'green' : rule.evidenceStatus === 'partial' ? 'yellow' : 'gray'}
                            size="xs"
                          >
                            {rule.evidenceStatus}
                          </Badge>
                        </Group>
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Stack gap="xs">
                        {rule.description && (
                          <Text size="xs" c="dimmed">{rule.description}</Text>
                        )}
                        <Group gap="xs" mt={4}>
                          <Code fz="xs">{rule.ruleId}</Code>
                          {rule.sectionRef && sl?.url && (
                            <Anchor
                              href={`${sl.url}#${rule.sectionRef}`}
                              target="_blank"
                              size="xs"
                            >
                              View § {rule.sectionRef} ↗
                            </Anchor>
                          )}
                        </Group>
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>
            </DetailSection>
          )}

          {/* Override Policy */}
          {ruleset.overridePolicy && (
            <DetailSection title="Override Policy">
              <Stack gap={6}>
                <Group gap="xs">
                  <Text size="xs" c="dimmed" fw={500}>Direction:</Text>
                  <Badge size="xs" variant="light" color={ruleset.overridePolicy.direction === 'tenant_wins' ? 'blue' : 'violet'}>
                    {ruleset.overridePolicy.direction}
                  </Badge>
                </Group>
                {ruleset.overridePolicy.conflictResolution && (
                  <Group gap="xs">
                    <Text size="xs" c="dimmed" fw={500}>Conflict resolution:</Text>
                    <Text size="xs">{ruleset.overridePolicy.conflictResolution}</Text>
                  </Group>
                )}
                {ruleset.overridePolicy.documentation && (
                  <Text size="xs" c="dimmed">{ruleset.overridePolicy.documentation}</Text>
                )}
              </Stack>
            </DetailSection>
          )}

          {/* Affected Workflows — clickable navigation (Gap #8) */}
          {ruleset.affectsWorkflows.length > 0 && (
            <DetailSection title="Affected Workflows">
              <Group gap={4}>
                {ruleset.affectsWorkflows.map((wf) => (
                  <Badge
                    key={wf}
                    variant="light"
                    size="sm"
                    component="a"
                    href={`/ferp-core/workflows/${wf}`}
                    style={{ cursor: 'pointer', textDecoration: 'none' }}
                    title={`Navigate to workflow ${wf}`}
                  >
                    {wf}
                  </Badge>
                ))}
              </Group>
            </DetailSection>
          )}

          {/* Trigger Workflows — clickable navigation (Gap #8) */}
          {ruleset.triggerWorkflows.length > 0 && (
            <DetailSection title="Triggered Workflows">
              <Group gap={4}>
                {ruleset.triggerWorkflows.map((wf) => (
                  <Badge
                    key={wf}
                    variant="light"
                    size="sm"
                    color="orange"
                    component="a"
                    href={`/ferp-core/workflows/${wf}`}
                    style={{ cursor: 'pointer', textDecoration: 'none' }}
                    title={`Navigate to triggered workflow ${wf}`}
                  >
                    {wf}
                  </Badge>
                ))}
              </Group>
            </DetailSection>
          )}

          {/* Compliance Frameworks */}
          {ruleset.complianceFrameworks.length > 0 && (
            <DetailSection title="Compliance Frameworks">
              <Group gap={4}>
                {ruleset.complianceFrameworks.map((fw) => (
                  <Badge key={fw} variant="outline" size="sm" color="teal">{fw}</Badge>
                ))}
              </Group>
            </DetailSection>
          )}

          {/* Industries */}
          {ruleset.appliesToIndustries.length > 0 && (
            <DetailSection title="Applies to Industries">
              <Group gap={4}>
                {ruleset.appliesToIndustries.map((ind) => (
                  <Badge key={ind} variant="light" size="sm" color="blue">{ind}</Badge>
                ))}
              </Group>
            </DetailSection>
          )}

          {/* Affected Form Schemas — clickable navigation (Gap #8) */}
          {ruleset.affectsFormSchemas.length > 0 && (
            <DetailSection title="Affected Form Schemas">
              <Group gap={4}>
                {ruleset.affectsFormSchemas.map((fs) => (
                  <Badge key={fs} variant="light" size="sm" color="violet" title={`Form schema: ${fs}`}>
                    {fs}
                  </Badge>
                ))}
              </Group>
            </DetailSection>
          )}

          {/* Dependencies — clickable navigation (Gap #8) */}
          {ruleset.dependsOn.length > 0 && (
            <DetailSection title="Depends On">
              <Group gap={4}>
                {ruleset.dependsOn.map((dep) => (
                  <Badge key={dep} variant="outline" size="sm" color="gray" title={`Dependency: ${dep}`}>
                    {dep}
                  </Badge>
                ))}
              </Group>
            </DetailSection>
          )}

        </Stack>
      </ScrollArea>
    </Stack>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================

function chipColor(dimension: string, value: string): string {
  if (dimension === 'safety_level') {
    if (value === 'critical') return 'red';
    if (value === 'high') return 'orange';
    if (value === 'elevated') return 'yellow';
  }
  if (dimension === 'status') {
    if (value === 'active') return 'green';
    if (value === 'superseded') return 'gray';
    if (value === 'pending') return 'yellow';
  }
  if (dimension === 'jurisdiction') return 'violet';
  if (dimension === 'facility_type') return 'blue';
  if (dimension === 'applies_when') return 'teal';
  if (dimension === 'legislation') return 'indigo';
  return 'gray';
}

export function RuleSetViewerPage() {
  const { data, loading, error } = useListRuleSetsQuery();
  const { dimensions: filterDimensions, loading: dimsLoading } = useComplianceFilterDimensions();
  const [selected, setSelected] = useState<RuleSetItem | null>(null);
  const [filters, setFilters] = useState<Record<string, string[]>>({});

  const rulesets = useMemo(() => (data?.ruleSets ?? []) as RuleSetItem[], [data]);

  const toggleFilter = useCallback((dimension: string, value: string) => {
    setFilters((prev) => {
      const current = prev[dimension] ?? [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      if (next.length === 0) {
        const { [dimension]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [dimension]: next };
    });
  }, []);

  const clearFilters = useCallback(() => setFilters({}), []);

  const filtered = useMemo(() => {
    let items = rulesets;
    const jurs = filters['jurisdiction'];
    if (jurs?.length) items = items.filter((rs) => jurs.includes(rs.jurisdiction));
    const fts = filters['facility_type'];
    if (fts?.length) items = items.filter((rs) => rs.appliesToIndustries.some((i) => fts.includes(i)));
    const sls = filters['safety_level'];
    if (sls?.length) items = items.filter((rs) => sls.includes(rs.safetyLevel?.toLowerCase()));
    const aws = filters['applies_when'];
    if (aws?.length) items = items.filter((rs) => ((rs as Record<string, unknown>).appliesWhenFlags as string[] ?? []).some((f: string) => aws.includes(f)));
    const legs = filters['legislation'];
    if (legs?.length) items = items.filter((rs) => legs.some((l) => (rs.sourceLegislation?.shortCite ?? '').includes(l)));
    const statuses = filters['status'];
    if (statuses?.length) {
      items = items.filter((rs) => {
        const status = rs.supersededBy ? 'superseded' : rs.isCurrentlyEffective ? 'active' : 'pending';
        return statuses.includes(status);
      });
    }
    return items;
  }, [rulesets, filters]);

  const stats = useMemo(() => ({
    total: filtered.length,
    lifeCritical: filtered.filter((rs) => rs.isLifeCritical).length,
    totalRules: filtered.reduce((acc, rs) => acc + rs.ruleCount, 0),
    currentlyEffective: filtered.filter((rs) => rs.isCurrentlyEffective).length,
    superseded: filtered.filter((rs) => rs.supersededBy).length,
  }), [filtered]);

  const handleNavigate = useCallback((rs: RuleSetItem) => setSelected(rs), []);
  const handleClose = useCallback(() => setSelected(null), []);
  const hasFilters = Object.keys(filters).length > 0;

  if (error) {
    return (
      <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error" m="xl">
        Failed to load regulatory framework: {error.message}
      </Alert>
    );
  }

  return (
    <Stack gap="md" h="100%" style={{ padding: '16px 20px' }}>
      {/* Header */}
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={2}>Regulatory Framework</Title>
          <Text c="dimmed" size="sm">Legislation and compliance rules governing this facility</Text>
        </div>
      </Group>

      {/* Stats */}
      <SimpleGrid cols={{ base: 2, sm: 4 }}>
        <Card withBorder padding="sm">
          <Group justify="space-between" mb={4}>
            <Text size="xs" c="dimmed">RuleSets</Text>
            <ThemeIcon variant="light" size="sm" color="blue"><IconScale size={13} /></ThemeIcon>
          </Group>
          <Text size="xl" fw={700}>{stats.total}</Text>
        </Card>
        <Card withBorder padding="sm">
          <Group justify="space-between" mb={4}>
            <Text size="xs" c="dimmed">Life-Critical</Text>
            <ThemeIcon variant="light" size="sm" color={stats.lifeCritical > 0 ? 'red' : 'gray'}>
              <IconAlertTriangle size={13} />
            </ThemeIcon>
          </Group>
          <Text size="xl" fw={700} c={stats.lifeCritical > 0 ? 'red' : undefined}>{stats.lifeCritical}</Text>
        </Card>
        <Card withBorder padding="sm">
          <Group justify="space-between" mb={4}>
            <Text size="xs" c="dimmed">Active Rules</Text>
            <ThemeIcon variant="light" size="sm" color="green"><IconShieldCheck size={13} /></ThemeIcon>
          </Group>
          <Text size="xl" fw={700}>{stats.totalRules}</Text>
        </Card>
        <Card withBorder padding="sm">
          <Group justify="space-between" mb={4}>
            <Text size="xs" c="dimmed">Effective Now</Text>
            <ThemeIcon variant="light" size="sm" color="cyan"><IconClock size={13} /></ThemeIcon>
          </Group>
          <Text size="xl" fw={700}>
            {stats.currentlyEffective}
            {stats.superseded > 0 && <Text span size="xs" c="dimmed" ml={4}>({stats.superseded} superseded)</Text>}
          </Text>
        </Card>
      </SimpleGrid>

      {/* Filter bar — backend-driven (DRR-055/074, no hardcoded values) */}
      <Paper withBorder px="sm" py="xs">
        <Stack gap={6}>
          <Group gap="sm" align="center" justify="space-between">
            <Group gap={6}>
              <IconFilter size={14} color="var(--mantine-color-dimmed)" />
              <Text size="xs" fw={600} c="dimmed">Filter by legislation, jurisdiction, type:</Text>
            </Group>
            {hasFilters && (
              <Tooltip label="Clear all filters">
                <ActionIcon size="xs" variant="subtle" color="gray" onClick={clearFilters}>
                  <IconX size={12} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
          {dimsLoading ? (
            <Loader size="xs" />
          ) : (
            filterDimensions.map((dim) => (
              <Group key={dim.dimension} gap={6} align="center" wrap="wrap">
                <Text size="xs" c="dimmed" fw={500} style={{ minWidth: 90 }}>{dim.label}:</Text>
                {dim.values.map((val) => {
                  const isActive = (filters[dim.dimension] ?? []).includes(val.value);
                  return (
                    <Badge
                      key={val.value}
                      size="sm"
                      variant={isActive ? 'filled' : 'light'}
                      color={chipColor(dim.dimension, val.value)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => toggleFilter(dim.dimension, val.value)}
                    >
                      {val.label} · {val.count}
                    </Badge>
                  );
                })}
              </Group>
            ))
          )}
        </Stack>
      </Paper>

      {/* Master-Detail layout */}
      {loading ? (
        <Group justify="center" py="xl"><Loader /></Group>
      ) : (
        <Paper withBorder style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <MasterDetailLayout
            storageKey="ruleset-viewer"
            emptyDetailMessage="Select a RuleSet to view legislation details"
            listContent={
              <RuleSetTable rulesets={filtered} onSelect={setSelected} selected={selected} />
            }
            detailContent={
              selected ? (
                <RuleSetDetail
                  ruleset={selected}
                  allRulesets={rulesets}
                  onNavigate={handleNavigate}
                  onClose={handleClose}
                />
              ) : null
            }
          />
        </Paper>
      )}
    </Stack>
  );
}

export default RuleSetViewerPage;
